import { computedFrom, autoinject } from 'aurelia-framework';
import { Customer, Payment, Account } from '../models/customer';
import { Features } from '../models/features';
import { I18N } from 'aurelia-i18n';
import { BasePage } from '../pages/base-page';
import { EventAggregator } from 'aurelia-event-aggregator';
import * as JWT from 'jwt-decode';
import { HPFServiceClient  } from './hpfApi';
import { State } from './global-state';

@autoinject
export class SessionManager {
	sessionTimer: any;
	logoutTimer: any;
	expireInMinutes: number;
	modalTimeoutMs: number = 30 * 1000;
	intervalID: number;
	readonly KEYCODE_SPACE = 32;
	router: any;
	readonly KEYCODE_TAB = 9;
	readonly KEYCODE_ESC = 27;

	constructor(private hpfService: HPFServiceClient, private state: State) {
		this.sessionTimer = null;
	}

	endSession() {
		this.state.logout();
		this.state.business = null;
		this.state.token = '';
		this.stopSessionTimer();
	}

	resetSessionTimer(router: any, expireInMinutes?: number, updateToken?: boolean) {
		this.router = router;
		console.info('session restarted at:', new Date());
		let timeoutId = this.sessionTimer;
		while (timeoutId--) {
			window.clearTimeout(this.sessionTimer);
		}

		// Update token if the business exists and has one already
		// The skip prevents infinite loops on token update service
		if (this.state.token && updateToken) {
			this.hpfService.updateJwtToken();
		}

		this.expireInMinutes = expireInMinutes || 19.5;

		let expireInMs = 1000 * 60 * this.expireInMinutes;
		this.sessionTimer = window.setTimeout(() => {
			this.displayWarningModal(this.modalTimeoutMs);
		}, expireInMs);
	}

	displayWarningModal(timeoutMs: number): void {
		const modal = document.querySelector('timeout-modal') as HTMLElement;
		const modalTimerText = modal.querySelector('#modalTimerText') as HTMLLabelElement;
		const modalExtendButton = modal.querySelector('#modalExtendButton') as HTMLButtonElement;
		const modalEndButton = modal.querySelector('#modalEndButton') as HTMLButtonElement;
		const secs = (timeoutMs / 1000).toString(); // 30.000

		modalExtendButton.addEventListener('click', (e: MouseEvent) => {
			this.extendSession();
			window.removeEventListener('click', this.extendSession);
		});

		modalEndButton.addEventListener('click', (e: MouseEvent) => {
			this.cleanupTimeout();
			window.removeEventListener('click', this.cleanupTimeout);
		});

		// set actual logout timer
		this.logoutTimer = window.setTimeout(() => {
			this.cleanupTimeout();
		}, timeoutMs);

		modalTimerText.innerHTML = secs.split('.')[0];

		// set focus to modal
		// show modal w/ remaining time
		modal.style.display = 'block';

		modalExtendButton.focus();

		document.addEventListener('keydown', this.checkKeepFocus.bind(this));

		let runIter = function() {
			timeoutMs = timeoutMs - 1000;
			const secs = (timeoutMs / 1000).toString(); // 30.000
			modalTimerText.innerHTML = secs.split('.')[0];
		};

		// update text every second
		this.intervalID = setInterval(runIter, 1000);
	}

	hideModal() {
		const timeoutModal = document.querySelector('timeout-modal') as HTMLElement;
		window.removeEventListener('keydown', this.checkKeepFocus);
		timeoutModal.style.display = 'none';
	}

	extendSession(): void {
		this.hideModal();
		let tempIntervalId = this.intervalID;
		window.clearInterval(this.intervalID);
		while (tempIntervalId--) {
			window.clearInterval(tempIntervalId);
		}
		this.resetSessionTimer(this.router, this.expireInMinutes, this.shouldUpdateToken());
	}

	cleanupTimeout(): void {
		window.clearTimeout(this.logoutTimer);
		// remove window event listener
		// close modal
		this.hideModal();
		window.clearInterval(this.intervalID);
		console.info('session expired at:', new Date());
		this.router.navigateToRoute('401');
	}

	stopSessionTimer() {
		if (this.sessionTimer) {
			window.clearTimeout(this.sessionTimer);
			this.sessionTimer = null;
		}
	}

	shouldUpdateToken(): boolean {
		// console.log(new Error().stack);
		let update = false;
		if (this.state.business && this.state.token) {
			let jwt = JWT(this.state.token);
			let timeToExpire = this.getJwtTimeToExpire(jwt);

			// if session expires in a minute, renew
			update = ((60 * 1000) > timeToExpire);
		}
		return update;
	}

	checkKeepFocus(e: KeyboardEvent) {
		const isTabPressed = (e.key === 'Tab' || e.keyCode === this.KEYCODE_TAB);

		if (!isTabPressed) {
			return;
		}

		if (e.shiftKey) /* shift + tab */ {
			if (document.activeElement === this.firstModalFocusable) {
				this.lastModalFocusable.focus();
				e.preventDefault();
			}
		} else /* tab */ {
			if (document.activeElement === this.lastModalFocusable) {
				this.firstModalFocusable.focus();
				e.preventDefault();
			}
		}
	}

	// check if user is has active session
	isAuthenticated(): boolean {
		if (!this.state || !this.state.token) {
			return false;
		}

		let jwt = JWT(this.state.token);
		let timeToExpire = this.getJwtTimeToExpire(jwt);

		// if there is no ConsumerUserKey the anonymous token is being used and there is not a logged in user session
		return timeToExpire > 0 && jwt.ConsumerUserKey != null;
	}

	getJwtTimeToExpire(jwt): number {
		let expirationDate = new Date(jwt.exp * 1000); // exp time is not in milliseconds
		return expirationDate.valueOf() - (new Date()).valueOf();
	}

	get firstModalFocusable() {
		return this.modalFocusables[0];
	}

	get lastModalFocusable() {
		return this.modalFocusables[this.modalFocusables.length - 1];
	}

	get modalFocusables(): HTMLElement[] {
		const modal = document.querySelector('timeout-modal') as HTMLElement;
		const modalExtendButton = modal.querySelector('#modalExtendButton') as HTMLButtonElement;
		const modalEndButton = modal.querySelector('#modalEndButton') as HTMLButtonElement;

		return [modalExtendButton, modalEndButton];
	}
}
